import { Accordion, Group, RingProgress, Stack, Text } from "@mantine/core"
import { InformationRequestStatus } from "@tfg/db"
import { Asterisk } from "@tfg/ui"
import { useMemo } from "react"

import { CustomFieldValueRequirement } from "@tfg/types/endpoints/traceability"

import { ActivityProgressIcon } from "~/pages/SupplierPortal/TraceabilityRequests/components/ActivityProgressIcon"
import { CustomFieldInput } from "~/pages/SupplierPortal/TraceabilityRequests/components/CustomFieldInput"
import { ItemTypeInformation } from "~/pages/SupplierPortal/TraceabilityRequests/components/OperationItemInformation"
import { useInformationRequestContext } from "~/pages/SupplierPortal/TraceabilityRequests/store/InformationRequestContext"
import {
  isCustomFieldValueComplete,
  OperationItem,
} from "~/pages/SupplierPortal/TraceabilityRequests/utils"
import { useGetTranslation } from "~/utils/translations"

type Props = {
  operationItem: OperationItem
  operationItems: OperationItem[]
  doNotKnow: boolean
  status: InformationRequestStatus
  isLoading: boolean
}

export const ExtraCustomFields = ({
  operationItem,
  operationItems,
  doNotKnow,
  status,
  isLoading,
}: Props) => {
  const extraCustomFieldDefOperationItems = useMemo(
    () =>
      operationItems.flatMap((oi) => {
        const extraCustomFieldDefs = operationItem.extraCustomFieldDefs.filter(
          (cfd) => cfd.forActivityId === oi.activityId
        )

        if (extraCustomFieldDefs.length > 0) {
          return {
            ...oi,
            customFieldDefs: extraCustomFieldDefs,
          }
        }
        return []
      }),
    [operationItems, operationItem.extraCustomFieldDefs]
  )

  if (
    !operationItem.extraCustomFieldDefs ||
    operationItem.extraCustomFieldDefs.length === 0
  ) {
    return null
  }

  return (
    <Accordion styles={{ content: { padding: 0 } }}>
      {extraCustomFieldDefOperationItems.map((operationItem) => {
        return (
          <ExtraCustomField
            key={operationItem.item.id}
            operationItem={operationItem}
            doNotKnow={doNotKnow}
            status={status}
            isLoading={isLoading}
          />
        )
      })}
    </Accordion>
  )
}

type ExtraCustomFieldProps = {
  operationItem: OperationItem
  doNotKnow: boolean
  status: InformationRequestStatus
  isLoading: boolean
}

export const ExtraCustomField = ({
  operationItem,
  doNotKnow,
  status,
  isLoading,
}: ExtraCustomFieldProps) => {
  const label = useGetTranslation(operationItem.item.itemInformation.type)
  const { progress, containesRequiredCustomFields } =
    useCalculateProgress(operationItem)

  return (
    <Accordion.Item
      key={operationItem.item.id}
      value={operationItem.item.id.toString()}
      bg="var(--mantine-color-white)"
    >
      <Accordion.Control p={0} bg="var(--mantine-color-white)">
        <Group>
          <RingProgress
            size={32}
            roundCaps
            styles={{
              label: {
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              },
            }}
            thickness={3}
            sections={[{ value: progress, color: "primary" }]}
            label={
              <ActivityProgressIcon
                progress={progress}
                activityName={operationItem.activityName}
                size={16}
              />
            }
          />
          <ItemTypeInformation
            item={operationItem.item}
            customLabel={
              <Group gap={2}>
                <Text>{label}</Text>
                {containesRequiredCustomFields && <Asterisk />}
              </Group>
            }
            showTitle={false}
          />
        </Group>
      </Accordion.Control>
      <Accordion.Panel p={0}>
        <Stack>
          {operationItem.customFieldDefs.map((cfd) => {
            return (
              <CustomFieldInput
                key={cfd.id}
                status={status}
                cfd={cfd}
                operationItem={operationItem}
                doNotKnow={doNotKnow}
                isLoading={isLoading}
              />
            )
          })}
        </Stack>
      </Accordion.Panel>
    </Accordion.Item>
  )
}

const useCalculateProgress = (operationItem: OperationItem) => {
  const { customFieldValues } = useInformationRequestContext()

  const completedHiddenCustomFields = useMemo(() => {
    return operationItem.customFieldDefs.filter((cfd) => {
      const fieldValue = customFieldValues
        .filter((cfv) => cfv.operationId === operationItem.operation?.id)
        .find((cfv) => cfv.customFieldDefinitionId === cfd.id)
      if (!fieldValue) return false
      return isCustomFieldValueComplete(
        fieldValue.value,
        cfd.documentRequirement,
        fieldValue.document
      )
    }).length
  }, [
    customFieldValues,
    operationItem.operation?.id,
    operationItem.customFieldDefs,
  ])

  const totalHiddenCustomFields = operationItem.hiddenCustomFieldDefs.length
  const progress = (completedHiddenCustomFields / totalHiddenCustomFields) * 100
  const containesRequiredCustomFields = operationItem.customFieldDefs.some(
    (cfd) => cfd.valueRequirement === CustomFieldValueRequirement.REQUIRED
  )

  return {
    progress,
    containesRequiredCustomFields,
  }
}
